import { t } from 'i18next';
import { useEffect, useState } from 'react';

import ChartTooltip from '../../components/Tooltip/ChartTooltip';
import { CommonFlex, CommonText } from '../../styledComponents/common';
import {
  AbsoluteScaleContainer,
  Bar,
  CenteredText,
  HorizontalScale,
  LegendColor,
  LegendContainer,
  LegendItem,
  LegendText,
  ScrollableContainer,
  VerticalLabel,
  VerticalScale,
} from './BarChartStyles';

const BarChart = ({ data = [], isLoading, xAxisLabel, maxValue = 100 }) => {
  const [max, setMax] = useState(maxValue);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState(null);

  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      // Calculate max as the total sum across all offices for each metric
      let maxValue = 0;

      // First collect all metrics across all offices
      data.forEach(item => {
        const sum =
          (item.complete || 0) +
          (item.partially_complete || 0) +
          (item.to_do || 0) +
          (item.totally_missed || 0) +
          (item.in_progress || 0) +
          (item.not_completed || 0);
        maxValue = Math.max(maxValue, sum);
      });
      setMax(Math.ceil(maxValue / 10) * 10);
    }
  }, [data, maxValue]);

  const handleMouseEnter = (event, item, key, label) => {
    const rect = event.target.getBoundingClientRect();
    const text = `${label}: ${item[key] || 0}`;

    setTooltipContent(text);
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleBarMouseEnter = (event, item) => {
    const rect = event.target.getBoundingClientRect();

    // Create tooltip content with all metrics
    let content = `<strong>${item.office || ''}</strong><br/>`;
    activeBarConfig.forEach(config => {
      content += `${config.label}: ${item[config.key] || 0}<br/>`;
    });

    setTooltipContent(content);
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setTooltipPosition(null);
  };

  // Define bar colors and keys with labels based on the data format
  const barConfig = [
    { key: 'complete', color: '#196B24', label: t('complete') },
    { key: 'partially_complete',color: '#E97132',label: t('partially_complete')},
    { key: 'in_progress', color: '#0078D4', label: t('in_progress') },
    { key: 'to_do', color: '#156082', label: t('to_do') },
    { key: 'not_completed', color: '#000000', label: t('not_completed') },
    { key: 'totally_missed', color: '#D83B01', label: t('totally_missed') }
  ];

  // Get keys from the first data item to determine which bars to show
  const dataKeys =
    data.length > 0
      ? Object.keys(data[0]).filter(
          key =>
            key !== 'office' &&
            key !== 'id' &&
            key !== 'name' &&
            key !== 'label' &&
            !key.includes('_id') &&
            !key.includes('_name'),
        )
      : [];

  // Map the data keys to the bar configuration (keep only the keys present in data)
  const activeBarConfig = barConfig.filter(config =>
    dataKeys.includes(config.key),
  );

  // Check if data is empty or undefined
  const hasData =
    data &&
    Array.isArray(data) &&
    data.length > 0 &&
    dataKeys &&
    dataKeys.length > 0;

  if (!hasData) {
    return (
      <NoDataComponent
        title={t('no_tasks_found')}
        padding="5vh 0"
        imageHeight="140px"
      />
    );
  }

  return (
    <>
      <ChartTooltip
        content={<div dangerouslySetInnerHTML={{ __html: tooltipContent }} />}
        position={tooltipPosition}
      />

      <LegendContainer>
        {activeBarConfig.map(bar => (
          <LegendItem key={bar.key}>
            <LegendColor color={bar.color} />
            <LegendText>{bar.label}</LegendText>
          </LegendItem>
        ))}
      </LegendContainer>
      <div
        style={{
          position: 'relative',
          height: 0,
          width: '100%',
          margin: 0,
        }}
      >
        <VerticalLabel>{t('number_of_tasks')}</VerticalLabel>
      </div>
      <ScrollableContainer>
        <CommonFlex
          gap="20px"
          alignItems="flex-start"
          justifyContent="start"
          style={{ height: '100%' }}
        >
          <CommonFlex direction="column" gap="20px" alignItems="flex-start">
            {Array.from({ length: 11 }, (_, index) => {
              const v = (10 - index) * (max / 10);
              return <VerticalScale key={v}>{Math.round(v)}</VerticalScale>;
            })}
          </CommonFlex>
          <CommonFlex
            style={{
              height: 'calc(100% - 24px)',
              marginTop: '11px',
              zIndex: 2,
              position: 'relative',
              width: '100%',
              padding: '0',
            }}
            gap="30px"
            justifyContent="flex-start"
          >
            {data.map((item, index) => (
              <CommonFlex
                direction="column"
                alignItems="bottom"
                justifyContent="flex-end"
                key={`bar-${index}-${item.id || index}`}
                style={{ height: '100%', position: 'relative' }}
              >
                <CommonText
                  name
                  title
                  fontSize="12px"
                  style={{
                    position: 'absolute',
                    bottom: '-20px',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {item.office ||
                    item.name ||
                    item.label ||
                    `Item ${index + 1}`}
                </CommonText>
                <CommonFlex
                  direction="column"
                  gap="0"
                  style={{ height: '100%', cursor: 'pointer' }}
                  alignItems="bottom"
                  justifyContent="flex-end"
                  onMouseEnter={e => handleBarMouseEnter(e, item)}
                  onMouseLeave={handleMouseLeave}
                >
                  {activeBarConfig.map(barItem => (
                    <Bar
                      key={barItem.key}
                      bgColor={barItem.color}
                      height={`${(100 * (item[barItem.key] || 0)) / max}%`}
                      onMouseEnter={e =>
                        handleMouseEnter(e, item, barItem.key, barItem.label)
                      }
                      onMouseLeave={handleMouseLeave}
                    />
                  ))}
                </CommonFlex>
              </CommonFlex>
            ))}
          </CommonFlex>
        </CommonFlex>
        <AbsoluteScaleContainer direction="column" gap="20px">
          {Array.from({ length: 11 }, (_, index) => {
            const v = (10 - index) * (max / 10);
            return (
              <HorizontalScale
                key={v}
                width={
                  data?.length > 0
                    ? `${Math.max(60 * data.length, 700)}px`
                    : '100%'
                }
              />
            );
          })}
        </AbsoluteScaleContainer>
      </ScrollableContainer>
      <CenteredText>{xAxisLabel || t('offices')}</CenteredText>
    </>
  );
};

export default BarChart;
